import React from "react";
import WSGCheck from "../../components/Atomos/Icons/WSGCheck";
import Button from "../../components/staff/Button";
import Close from "../../components/Atomos/Icons/Close";
import SaveChanges from "../../components/Atomos/Icons/EditMode/SaveChanges";
import { todayIso } from "../../utils/dates";

export { todayIso };

/** Positive office balance (debt owed to WSG). */
export const MEMBERSHIP_BALANCE_LABEL = "Membership Balance";
/** Negative office balance (credit available to apply). */
export const MEMBER_BALANCE_CREDIT_LABEL = "Member Balance";

/** YYYY-MM-DD -> MM/DD/YYYY (calendar parts only, no timezone shift). */
export function isoToDisplay(iso: string): string {
  const match = String(iso ?? "")
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return "";
  }
  return `${match[2]}/${match[3]}/${match[1]}`;
}

/** MM/DD/YYYY (from CalendarInput) -> YYYY-MM-DD using local calendar date parts. */
export function displayToIso(display: string): string {
  const trimmed = display.trim();
  if (!trimmed) {
    return todayIso();
  }

  const mmddyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (mmddyyyy) {
    const month = mmddyyyy[1].padStart(2, "0");
    const day = mmddyyyy[2].padStart(2, "0");
    return `${mmddyyyy[3]}-${month}-${day}`;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return todayIso();
  }
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatMoney(value: number): string {
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function AccountingModalFirmSubtitle({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return <p className="-mt-1 text-base text-gray-font">{children}</p>;
}

export function AccountingModalHeader({
  title,
  firmName,
  jurisdiction,
}: {
  title?: string;
  firmName: string;
  jurisdiction?: string;
}): React.ReactElement {
  const jurisdictionLabel = String(jurisdiction ?? "").trim();

  return (
    <div className="-mt-1 flex flex-col gap-1 border-b border-gray-200 pb-4">
      {title ? (
        <h2 className="font-decimal text-[26px] font-medium leading-[36px] text-primary">
          {title}
        </h2>
      ) : null}
      <p className="text-lg font-semibold leading-tight text-gray-font md:text-xl">
        {firmName}
      </p>
      {jurisdictionLabel ? (
        <p className="text-sm font-normal text-gray-font md:text-base">
          {jurisdictionLabel}
        </p>
      ) : null}
    </div>
  );
}

export function AccountingModalTabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { key: T; label: string; disabled?: boolean }[];
  active: T;
  onChange: (key: T) => void;
}): React.ReactElement {
  return (
    <div className="flex flex-wrap gap-1" role="tablist">
      {tabs.map(({ key, label, disabled }) => {
        const isActive = active === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={disabled}
            onClick={() => {
              if (!disabled) {
                onChange(key);
              }
            }}
            className={
              disabled
                ? "cursor-not-allowed rounded px-4 py-2 text-sm font-semibold bg-gray-100 text-gray-400"
                : isActive
                  ? "rounded px-4 py-2 text-sm font-semibold bg-accent-Default-dark text-white"
                  : "rounded px-4 py-2 text-sm font-semibold bg-[#E4F3FF] text-[#0171CC]"
            }
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function AccountingModalSectionTitle({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <h3 className="text-primary font-decimal text-[26px] font-medium leading-[36px]">
      {children}
    </h3>
  );
}

export function AccountingModalCheckbox({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  disabled?: boolean;
}): React.ReactElement {
  const toggle = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  return (
    <div className="flex flex-row items-center gap-2">
      <div
        onClick={toggle}
        className={`stroke-gray-font stroke-2 ${
          !checked ? "fill-neutral" : "fill-secondary"
        } h-5 w-5 min-h-[20px] min-w-[20px] ${
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        }`}
      >
        <WSGCheck check={checked} />
      </div>
      <p
        className={`text-base font-normal text-gray-font ${
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        }`}
        onClick={toggle}
      >
        {label}
      </p>
    </div>
  );
}

export function AccountingModalSummaryPanel({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="rounded-lg border border-gray-200 bg-neutral p-4">
      {children}
    </div>
  );
}

export function AccountingAssessmentMergedNotice(): React.ReactElement {
  return (
    <div className="rounded border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
      <span className="font-medium">Notice:</span> This Assessment invoice has
      been merged into the Annual Invoice.
    </div>
  );
}

export function AccountingInvoiceMergeStatusNote({
  annualInvoiceRef,
  mergeDate,
}: {
  annualInvoiceRef: string;
  mergeDate: string;
}): React.ReactElement {
  const invoiceRef = annualInvoiceRef.trim() || "Pending";
  const dateLabel = mergeDate.trim() || "—";

  return (
    <div className="rounded-lg border border-gray-200 bg-neutral p-4">
      <p className="text-sm leading-relaxed text-gray-font">
        <span className="font-medium">Invoice Status Note:</span> Merged with
        Annual Invoice{" "}
        <span className="break-all font-semibold">[{invoiceRef}]</span> on{" "}
        <span className="font-semibold">{dateLabel}</span>.
      </p>
    </div>
  );
}

export function AccountingModalSummaryLine({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-4">
      <span
        className={
          strong
            ? "text-base font-medium text-gray-font"
            : "text-base font-normal text-gray-font"
        }
      >
        {label}
      </span>
      <span
        className={`tabular-nums text-gray-font ${
          strong ? "text-xl font-medium" : "text-base font-medium"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

export function AccountingModalError({
  message,
}: {
  message: string;
}): React.ReactElement | null {
  if (!message) {
    return null;
  }
  return <p className="text-base text-primary">{message}</p>;
}

type AccountingModalFooterProps = {
  onCancel: () => void;
  onSubmit: () => void;
  submitText: string;
  saving: boolean;
  cancelText?: string;
  submitColor?: "accent" | "primary";
  extraLeft?: React.ReactNode;
  submitDisabled?: boolean;
};

export function AccountingModalFooter({
  onCancel,
  onSubmit,
  submitText,
  saving,
  cancelText = "Cancel",
  submitColor = "accent",
  extraLeft,
  submitDisabled = false,
}: AccountingModalFooterProps): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
      <div className="min-w-0">{extraLeft}</div>
      <div className="flex flex-wrap gap-3">
        <Button
          text={cancelText}
          color="neutral"
          variant="outlined"
          size="fixed"
          icon={<Close />}
          onClick={onCancel}
          disabled={saving}
        />
        <Button
          text={saving ? "Saving..." : submitText}
          color={submitColor === "primary" ? "primary" : "secondary"}
          variant="filled"
          size="fixed"
          icon={<SaveChanges />}
          onClick={onSubmit}
          disabled={saving || submitDisabled}
        />
      </div>
    </div>
  );
}
